import { FC, Fragment, useMemo } from 'react';

import AcceptConflictBold from '@/assets/icons/AcceptConflictBold';
import { Conflict } from '@/types/Conflicten';
import { classNames } from '@/utils/classNames';

import usePlankDragSource from '../../hooks/usePlankDragSource';
import usePlankTableStore from '../../hooks/usePlankTableStore';
import {
  geefInzetVoorConflict,
  geefStelOfOorspronkelijkDienstVoorConflict,
  geefStelOfOorspronkelijkDienstVoorMancoConflict,
  geefVerkeersdagTijdstipVoorElement,
  isVerkeersdagVandaag,
} from '../../utils/conflict';
import { openstaandConflictToSelectionItem, toOpenstaandConflict } from '../../utils/plankSelectionItems';
import NotitieRelatedItem from '../Notitie/NotitieRelatedItem';
import SidepanelToggle from '../Sidepanel/Toggle/SidepanelToggle';

import ConflictParts from './ConflictParts';

import cardStyles from '../Table/Table.module.css';
import styles from './Conflict.module.css';

const ConflictListItem: FC<{ conflict: Conflict }> = ({ conflict }) => {
  const openstaandConflict = useMemo(() => toOpenstaandConflict(conflict), [conflict]);

  const relatedNotitie = usePlankTableStore((state) => state.getConflictNotitie(openstaandConflict));

  const element =
    openstaandConflict.type === 'MANCO' ? geefInzetVoorConflict(openstaandConflict) : openstaandConflict.elementen[0];
  const oorspronkelijkeDienst =
    openstaandConflict.type === 'MANCO'
      ? geefStelOfOorspronkelijkDienstVoorMancoConflict(element)
      : geefStelOfOorspronkelijkDienstVoorConflict(openstaandConflict);

  const selectionItem = useMemo(() => openstaandConflictToSelectionItem(openstaandConflict), [openstaandConflict]);

  // Whether this row is currently open in the sidepanel (shown with a focus border). The drag
  // cursor should also appear here, not only on a Ctrl/Shift multi-selection.
  const isFocused = usePlankTableStore((state) =>
    state.isEenheidSelected({ eenheid: String(oorspronkelijkeDienst), conflict: openstaandConflict }),
  );

  const { isSelected, showDragCursor, dragProps, onSelectionClickCapture } = usePlankDragSource({
    item: selectionItem,
    testId: `draggableConflict_${openstaandConflict.conflictId}`,
    isFocused,
  });

  return (
    <SidepanelToggle
      conflict={openstaandConflict}
      eenheid={String(oorspronkelijkeDienst)}
      notitie={relatedNotitie}
      onClickCapture={onSelectionClickCapture}
    >
      <div
        className={classNames(
          cardStyles.cardStyleXl,
          isSelected && cardStyles.plankSelectedStyle,
          showDragCursor && cardStyles.plankDraggableStyle,
        )}
        {...dragProps}
      >
        <div className={styles.conflictItemStyle}>
          <ConflictParts.Indicator xl conflictType={openstaandConflict.type} />
          <div className={styles.flexCol}>
            <div className={styles.flex}>
              <div className={styles.flex}>
                <ConflictParts.Icon conflictType={openstaandConflict.type} />
                <div className={styles[`conflictHeaderStyle--${openstaandConflict.type}`]}>
                  {openstaandConflict.elementen.length > 0 ? (
                    oorspronkelijkeDienst
                  ) : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {openstaandConflict.lastAcceptedPerDay?.map((el, index) => (
                        <Fragment key={el.treinNummer}>
                          {index > 0 && <span style={{ padding: '0 3px' }}> - </span>}
                          {el.uitvoeringstijd && !isVerkeersdagVandaag(el) && (
                            <span style={{ fontWeight: 300, textTransform: 'lowercase' }}>
                              {geefVerkeersdagTijdstipVoorElement(el)}:
                            </span>
                          )}
                          <span style={{ fontWeight: 600 }}>
                            {openstaandConflict.type === 'VERKEERD_MATERIEEL' &&
                              geefStelOfOorspronkelijkDienstVoorConflict(openstaandConflict)}
                          </span>
                          {openstaandConflict.type === 'MANCO' && <>{el.oorspronkelijkeDienst}</>}
                        </Fragment>
                      ))}
                      <AcceptConflictBold width={16} height={16} fill='var(--tractie-color-content-emphasis)' />
                    </span>
                  )}
                </div>
                <ConflictParts.AssignedMat showRelatedDefect showDefectIcon conflict={openstaandConflict} />
                {openstaandConflict.elementen.length > 0 && (
                  <ConflictParts.OpvolgendeBijzonderheidscode
                    conflict={openstaandConflict}
                    huidigElement={element}
                    compact
                  />
                )}
              </div>
            </div>
            <div className={styles.flex}>
              {openstaandConflict.lastAcceptedPerDay?.length ? (
                <div className={styles.toelichtingStyle}>
                  <span style={{ fontStyle: 'oblique' }}>Volledig geaccepteerd conflict</span>
                </div>
              ) : (
                <ConflictParts.Toelichting element={element} />
              )}
              <NotitieRelatedItem notitie={relatedNotitie} inFlow />
            </div>
          </div>
        </div>
      </div>
    </SidepanelToggle>
  );
};
export default ConflictListItem;
